import React, { createContext, useContext, useEffect, useState } from 'react';
import { api, getToken, setToken } from './apiClient';

// Authentication against the backend (fastapi-users JWT). The context API is
// kept stable (login / register / logout / switchRole / switchPractice /
// session / loading) so consumers don't change. The session shape is
// { user, practice, practices, token }.

const AuthContext = createContext(null);
const LS_KEY = 'dh_session';

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // On boot, restore the cached session optimistically, then revalidate the
  // token against the server.
  useEffect(() => {
    const boot = async () => {
      const token = getToken();
      const raw = localStorage.getItem(LS_KEY);
      if (token && raw) {
        try { setSession(JSON.parse(raw)); } catch (e) { /* ignore */ }
        try {
          const { data } = await api.get('/auth/session');
          persist({ ...data, token });
        } catch (e) {
          setToken(null);
          localStorage.removeItem(LS_KEY);
          setSession(null);
        }
      }
      setLoading(false);
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = (next) => {
    localStorage.setItem(LS_KEY, JSON.stringify(next));
    setSession(next);
    return next;
  };

  const login = async (email, password) => {
    const form = new URLSearchParams();
    form.append('username', email);
    form.append('password', password);
    let token;
    try {
      const { data } = await api.post('/auth/jwt/login', form, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      token = data.access_token;
    } catch (e) {
      throw new Error('Invalid email or password');
    }
    setToken(token);
    const { data: sess } = await api.get('/auth/session');
    return persist({ ...sess, token });
  };

  const register = async ({ name, email, password, practiceName }) => {
    try {
      await api.post('/auth/register', { name, email, password, practiceName });
    } catch (e) {
      throw new Error(e.userMessage || 'Registration failed');
    }
    return login(email, password);
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem(LS_KEY);
    setSession(null);
  };

  // Role/practice switching are client-side view concerns over the session.
  const switchRole = (role) => {
    if (!session) return;
    persist({ ...session, user: { ...session.user, role } });
  };

  const switchPractice = (id) => {
    if (!session) return;
    const practice = id === 'all'
      ? { id: 'all', name: 'All practices' }
      : (session.practices || []).find((p) => p.id === id);
    if (!practice) return;
    persist({ ...session, practice });
  };

  return (
    <AuthContext.Provider value={{ session, loading, login, register, logout, switchRole, switchPractice }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
